import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Tooltip } from '@mui/material';
import { useAppDispatch } from 'store/hooks';
import { showToast } from 'store/toastSlice';
import { gamesService, GameStatsExportItem, GameStatsExportPeriod } from '../api/games';

interface GameStatsExportButtonsProps {
  siteId: number | null;
}

interface ActiveExport {
  exportId: number;
  period: GameStatsExportPeriod;
}

const POLL_INTERVAL_MS = 2500;
const MAX_POLL_ERRORS = 5;

const buttonSx = {
  bgcolor: '#2a8039',
  color: '#fff',
  textTransform: 'none',
  '&:hover': { bgcolor: '#2a8039', opacity: 0.9 },
  '&.Mui-disabled': { bgcolor: 'rgba(255, 255, 255, 0.1)', color: 'rgba(255, 255, 255, 0.3)' },
};

const exportFileName = (item: GameStatsExportItem) =>
  item.fileName || `game-statistics-${item.id}.xlsx`;

const isInProgress = (item: GameStatsExportItem) =>
  item.status === 'pending' || item.status === 'running';

const GameStatsExportButtons: React.FC<GameStatsExportButtonsProps> = ({ siteId }) => {
  const dispatch = useAppDispatch();
  const [active, setActive] = useState<ActiveExport | null>(null);
  const toast = useCallback(
    (text: string, type: 'success' | 'error' | 'info') => dispatch(showToast({ text, type })),
    [dispatch],
  );

  const siteIdRef = useRef(siteId);
  useEffect(() => {
    siteIdRef.current = siteId;
  }, [siteId]);

  const downloadFile = useCallback(
    async (item: GameStatsExportItem) => {
      try {
        const blob = await gamesService.downloadGameStatsExport(item.id);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', exportFileName(item));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast(`Downloaded ${exportFileName(item)}`, 'success');
      } catch (error: any) {
        toast(error.response?.data?.message || 'Download failed', 'error');
      }
    },
    [toast],
  );

  // Reset on site change, then re-attach to an in-flight export if one exists.
  useEffect(() => {
    setActive(null);
    if (siteId === null) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await gamesService.listGameStatsExports(siteId);
        const rows: GameStatsExportItem[] = res?.data || [];
        const running = rows.find(isInProgress);
        if (!cancelled && running) {
          setActive((prev) => prev ?? { exportId: running.id, period: running.period });
        }
      } catch (error) {
        console.error('Failed to list game stats exports', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [siteId]);

  const activeId = active?.exportId;
  useEffect(() => {
    if (activeId === undefined) return;
    let stopped = false;
    let inFlight = false;
    let errorCount = 0;
    const intervalId = setInterval(async () => {
      if (inFlight || stopped) return;
      inFlight = true;
      try {
        const res = await gamesService.getGameStatsExport(activeId);
        if (stopped) return;
        errorCount = 0;
        const item: GameStatsExportItem = res?.data;
        if (!item) return;
        if (item.status === 'completed') {
          stopped = true;
          clearInterval(intervalId);
          await downloadFile(item);
          setActive((prev) => (prev?.exportId === activeId ? null : prev));
        } else if (item.status === 'failed') {
          stopped = true;
          clearInterval(intervalId);
          toast(item.error || 'Export failed', 'error');
          setActive((prev) => (prev?.exportId === activeId ? null : prev));
        }
      } catch (error: any) {
        if (stopped) return;
        if (error.response?.status === 404) {
          stopped = true;
          clearInterval(intervalId);
          toast('Export no longer exists', 'error');
          setActive((prev) => (prev?.exportId === activeId ? null : prev));
        } else {
          errorCount += 1;
          if (errorCount >= MAX_POLL_ERRORS) {
            stopped = true;
            clearInterval(intervalId);
            toast('Lost contact with the export — please check the list and try again', 'error');
            setActive((prev) => (prev?.exportId === activeId ? null : prev));
          }
        }
      } finally {
        inFlight = false;
      }
    }, POLL_INTERVAL_MS);
    return () => {
      stopped = true;
      clearInterval(intervalId);
    };
  }, [activeId, downloadFile, toast]);

  const startExport = async (period: GameStatsExportPeriod) => {
    if (siteId === null) return;
    const requestedSiteId = siteId;
    try {
      const res = await gamesService.createGameStatsExport({ siteId, period });
      if (siteIdRef.current !== requestedSiteId) return;
      setActive({ exportId: res.data.exportId, period });
    } catch (error: any) {
      if (error.response?.status === 409) {
        try {
          const list = await gamesService.listGameStatsExports(siteId);
          if (siteIdRef.current !== requestedSiteId) return;
          const rows: GameStatsExportItem[] = list?.data || [];
          const existing = rows.find((r) => isInProgress(r) && r.period === period);
          if (existing) {
            setActive({ exportId: existing.id, period });
            toast('An export is already in progress — attaching to it', 'info');
            return;
          }
        } catch (listError) {
          console.error('Failed to list game stats exports', listError);
        }
        toast(error.response?.data?.message || 'Failed to start export', 'error');
        return;
      }
      toast(error.response?.data?.message || 'Failed to start export', 'error');
    }
  };

  const renderButton = (period: GameStatsExportPeriod, label: string) => {
    const isActive = active?.period === period;
    return (
      <Button
        variant="contained"
        sx={buttonSx}
        disabled={siteId === null || active !== null}
        onClick={() => startExport(period)}
      >
        {isActive ? (
          <>
            <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
            Preparing…
          </>
        ) : (
          label
        )}
      </Button>
    );
  };

  const buttons = (
    <Box sx={{ display: 'flex', gap: 1 }}>
      {renderButton('previous_month', 'Download previous month')}
      {renderButton('all_time', 'Download all time')}
    </Box>
  );

  if (siteId === null) {
    return (
      <Tooltip title="Select a site to export">
        <span>{buttons}</span>
      </Tooltip>
    );
  }
  return buttons;
};

export default GameStatsExportButtons;
